import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, AlertTriangle, Landmark, FileDown } from "lucide-react";
import { RecordCard, CardGrid, FilterStrip, StatusBadge, EmptyTile, SkeletonTiles, SectionForm, Field, KV, btn } from "@/components/bento";

const STATUS_COLOR = {
  "Verification Pending": "bg-amber-100 text-amber-800",
  Approved: "bg-blue-100 text-blue-800",
  Rejected: "bg-red-100 text-red-700",
  Transferred: "bg-emerald-100 text-emerald-800",
};

export default function Withdraw() {
  const dlInvoice = async (w) => {
    try {
      const r = await api.get(`/invoices/${w.gst_invoice_number}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${w.gst_invoice_number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Invoice download failed"); }
  };
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ amount: "", bank_account: "", ifsc: "", account_holder: "" });
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [m, w] = await Promise.all([api.get("/auth/me"), api.get("/withdrawals")]);
    setMe(m.data); setItems(w.data);
    setForm((f) => ({
      ...f,
      bank_account: m.data.bank_account || "",
      ifsc: m.data.ifsc || "",
      account_holder: m.data.account_holder || m.data.name || "",
    }));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    if (amt > (me?.wallet_balance || 0)) return toast.error("Insufficient balance");
    if (!form.bank_account || !form.ifsc || !form.account_holder) return toast.error("Fill all bank details");
    setSubmitting(true);
    try {
      await api.post("/withdrawals", { amount: amt, bank_account: form.bank_account, ifsc: form.ifsc.toUpperCase(), account_holder: form.account_holder });
      toast.success("Withdrawal request submitted!");
      setForm({ ...form, amount: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  if (!me) return <SkeletonTiles n={2} cols={2} h="h-48" testId="withdraw-loading" />;

  const approved = (me.profile_status || "Pending") === "Approved";
  const shown = items.filter((w) => filter === "all" || w.status === filter);
  const chips = [{ k: "all", label: "All", count: items.length }, ...Object.keys(STATUS_COLOR).map((s) => ({ k: s, label: s, count: items.filter((w) => w.status === s).length })).filter((c) => c.count > 0)];

  return (
    <div className="space-y-6" data-testid="withdraw-page">
      <div>
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">NEFT Payout</span>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-2">Withdraw</h1>
      </div>

      {!approved && (
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5 flex gap-4" data-testid="withdraw-blocked-banner">
          <Lock className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-black text-lg text-amber-900">Withdrawals locked</div>
            <p className="text-sm text-amber-800 mt-0.5">
              {me.profile_status === "Submitted"
                ? "Your profile is under review by the Operations team. You can request withdrawals once your profile is approved."
                : me.profile_status === "Rejected"
                ? "Your profile was rejected — please update it and resubmit to unlock withdrawals."
                : "Complete your profile (KYC + bank details) to unlock withdrawals."}
            </p>
            <Link to="/app/profile" className="inline-flex items-center gap-1.5 mt-2 text-sm font-bold text-emerald-900 hover:underline" data-testid="withdraw-goto-profile">
              <AlertTriangle className="w-3.5 h-3.5" /> Go to Profile →
            </Link>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-4 stagger">
        <div className="lg:col-span-2 space-y-4">
          <div className="bento-dark rounded-2xl border border-emerald-900 p-7" data-testid="balance-card">
            <div className="relative z-10">
              <div className="text-xs font-bold uppercase tracking-widest text-saffron-400">Available Balance</div>
              <div className="font-display text-5xl font-black mt-3">{formatINR(me.wallet_balance)}</div>
              <div className="text-xs text-white/60 mt-2">Pending withdrawals: {formatINR(me.pending_withdrawal || 0)}</div>
            </div>
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-5 grid grid-cols-2 gap-3">
            <KV label="Requests" value={items.length} mono />
            <KV label="Transferred" value={items.filter((w) => w.status === "Transferred").length} mono />
            <KV label="Profile" value={me.profile_status || "Pending"} />
            <KV label="Bank" value={me.bank_account ? `••${me.bank_account.slice(-4)}` : "Not set"} mono />
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-3">
          <SectionForm title="Request Withdrawal" desc="NEFT to your verified bank account. Requests are processed by Finance." icon={Landmark} testId="withdraw-form">
            <Field label="Amount (₹)" full>
              <Input data-testid="withdraw-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="h-12 text-lg font-mono font-bold" placeholder="1000" />
            </Field>
            <Field label="Account Holder Name" full>
              <Input data-testid="withdraw-account-holder" value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} className="h-11" />
            </Field>
            <Field label="Bank Account Number">
              <Input data-testid="withdraw-bank-account" value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} className="h-11 font-mono" />
            </Field>
            <Field label="IFSC Code">
              <Input data-testid="withdraw-ifsc" value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} className="h-11 font-mono uppercase" />
            </Field>
            <div className="sm:col-span-full pt-1">
              <Button type="submit" data-testid="withdraw-submit" disabled={submitting || !approved} className="w-full h-12 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold disabled:opacity-60">
                {submitting ? "Submitting..." : approved ? "Request NEFT Withdrawal" : "Profile approval required"}
              </Button>
            </div>
          </SectionForm>
        </form>
      </div>

      {items.length > 0 && <FilterStrip testId="withdraw-filter" chips={chips} active={filter} onChange={setFilter} />}
      <CardGrid cols={3} dense testId="withdrawals-table">
        {shown.length === 0 && <EmptyTile testId="withdrawals-empty" title="No withdrawal requests yet" desc="Your NEFT requests and their status will show up here." />}
        {shown.map((w) => (
          <RecordCard key={w.id} dense testId={`withdrawal-${w.id}`}
            title={formatINR(w.amount)} sub={`A/C ${w.bank_account?.slice(-4).padStart(w.bank_account.length, "•")} · ${w.ifsc}`}
            badge={<StatusBadge status={w.status} className={STATUS_COLOR[w.status]} />}
            meta={[<span>{new Date(w.created_at).toLocaleDateString()}</span>, w.payout?.utr && <span className="font-mono text-emerald-800" data-testid={`withdraw-utr-${w.id}`}>UTR {w.payout.utr}</span>, w.note && <span className="text-stone-500">{w.note}</span>]}
            actions={w.gst_invoice_number && (
              <button type="button" onClick={() => dlInvoice(w)} data-testid={`withdraw-gst-inv-${w.id}`} className={btn.ghost}>
                <FileDown className="w-3.5 h-3.5" /> GST Invoice {w.gst_invoice_number}
              </button>
            )} />
        ))}
      </CardGrid>
    </div>
  );
}
